import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import api from '../utils/api.util';
import toast from '../utils/toast.util';
import { useAuth } from '../auth/AuthContext';
import { ENDPOINT } from '../constants/common-app.const';

type CourseStatus = 'new' | 'created' | 'payed' | 'finished';

export interface MyCourseCard {
  id: string;
  uuid: string;
  courseId: string | null;
  title: string;
  createdAt: string;
  periodFrom: string;
  periodTo: string;
  amount: string;
  contractNumber: string;
  studentsCount: number;
  status: CourseStatus;
  isOwner: boolean;
  listenerCompleted: boolean;
  notice?: string;
}

const transformContract = (c: any): MyCourseCard => ({
  id: c.id,
  uuid: c.uuid,
  courseId: c.course?.id ?? null,
  title: c.course?.title ?? c.course?.name ?? 'Курс',
  createdAt: dayjs(c.createdAt).format('DD.MM.YYYY'),
  periodFrom: c.group?.startDate ? dayjs(c.group.startDate).format('DD.MM.YYYY') : '—',
  periodTo: c.group?.endDate ? dayjs(c.group.endDate).format('DD.MM.YYYY') : '—',
  amount: c.sumByn != null && !isNaN(parseFloat(c.sumByn))
    ? `${parseFloat(c.sumByn).toFixed(0)} BYN`
    : '—',
  contractNumber: c.contractNumber ?? 'Не присвоен',
  studentsCount: c.listenerCount,
  status: (new Set(['new', 'created', 'payed', 'finished']).has(c.status) ? c.status : 'new') as CourseStatus,
  isOwner: c.isOwner ?? true,
  listenerCompleted: c.listenerCompleted ?? false,
  notice: (c.status === 'new' && !c.contractNumber)
    ? 'Черновик договора — не подан'
    : undefined,
});

const useGetMyContracts = () => {
  const [contracts, setContracts] = useState<MyCourseCard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getJson(ENDPOINT.CONTRACTS_MINE);
      setContracts((Array.isArray(response) ? response : []).map(transformContract));
      setLoading(false);
    } catch (err) {
      toast.showOnFailure('Ошибка загрузки договоров');
      setError('fetch failed');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchContracts();
    }
  }, [isAuthenticated, fetchContracts]);

  return { contracts, loading, error };
};

export default useGetMyContracts;
